window.bootCartRecommendations = {
  swiper: null,
  isLoading: false,
  lastCartHash: '',
  recommendationLimit: 6,
  recommendationSeedLimit: 3,
  selectors: {
    inner: '#CartDrawer-Recommendations .cart-drawer-recommendations__inner',
  },

  getContainer() {
    return document.querySelector(this.selectors.inner);
  },

  getStrings() {
    const wrapper = document.getElementById('CartDrawer-Recommendations');
    const dataset = wrapper?.dataset || {};
    return {
      title: dataset.title || '',
      add: dataset.addLabel || '',
      adding: dataset.addingLabel || '',
      previousRecommendation: dataset.prevLabel || '',
      nextRecommendation: dataset.nextLabel || '',
    };
  },

  async init(force = false) {
    this.ensureAddDelegation();
    if (this.getContainer()) await this.load(force);
  },

  async load(force = false) {
    if (this.isLoading || !this.getContainer()) return;

    this.isLoading = true;
    try {
      const cart = await this.getCart();
      if (!cart || !cart.items || !cart.items.length) {
        this.clear();
        this.destroySwiper();
        this.lastCartHash = '';
        return;
      }

      const cartHash = this.getCartHash(cart);

      const cached = force ? [] : this.getCachedProducts(cartHash);
      if (cached.length) {
        this.render(cached.slice(0, this.recommendationLimit));
        this.initSwiper();
        this.lastCartHash = cartHash;
        return;
      }

      const products = await this.getRecommendationsFromCart(cart);
      if (!products.length) {
        this.clear();
        this.destroySwiper();
        this.lastCartHash = cartHash;
        return;
      }

      this.saveCache(cartHash, products);
      this.render(products.slice(0, this.recommendationLimit));
      this.initSwiper();
      this.lastCartHash = cartHash;
    } catch (error) {
      // Leave whatever is currently shown in place on a transient error
      // (e.g. a rate-limited refresh) instead of wiping the drawer.
      console.error('Cart recommendations error:', error);
    } finally {
      this.isLoading = false;
    }
  },

  getCache() {
    return window.CartRecommendationsCache || null;
  },

  getCachedProducts(cartHash) {
    return this.getCache()?.read(cartHash) || [];
  },

  saveCache(cartHash, products) {
    this.getCache()?.write(cartHash, products);
  },

  removeFromCache(variantId) {
    this.getCache()?.removeVariant(variantId);
  },

  render(products) {
    const container = this.getContainer();
    if (!container) return;
    const strings = this.getStrings();

    // Destroy any existing Swiper BEFORE replacing the markup. Otherwise the old
    // instance's mutation observers keep firing against detached nodes and pile
    // up across re-renders, which can lock up the drawer after a few adds.
    this.destroySwiper();

    container.innerHTML = `
      <div class="cart-upsell-block">
        <div class="cart-upsell-block__header">
          <h6 class="cart-upsell-block__title">${this.escapeHtml(strings.title)}</h6>
          <div class="cart-upsell-nav">
            <button type="button" class="cart-upsell-prev" aria-label="${this.escapeHtml(strings.previousRecommendation)}">
              <span aria-hidden="true">\u2039</span>
            </button>
            <button type="button" class="cart-upsell-next" aria-label="${this.escapeHtml(strings.nextRecommendation)}">
              <span aria-hidden="true">\u203A</span>
            </button>
          </div>
        </div>

        <div class="cart-upsell-swiper swiper">
          <div class="swiper-wrapper">
            ${products
              .map((product) => {
                const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
                if (!variant) return '';

                const image = this.getProductImage(product);
                const price = this.formatMoney(product.price);
                const compareAt =
                  product.compare_at_price && product.compare_at_price > product.price
                    ? this.formatMoney(product.compare_at_price)
                    : '';

                return `
                <div class="swiper-slide">
                  <div class="cart-upsell-card">
                    <a href="${product.url}" class="cart-upsell-card__image-link">
                      <img
                        class="cart-upsell-card__image"
                        src="${image}"
                        alt="${this.escapeHtml(product.title)}"
                        loading="lazy"
                        width="96"
                        height="96"
                      >
                    </a>
                    <div class="cart-upsell-card__content">
                      <a href="${product.url}" class="cart-upsell-card__title cart-item__name h4 break">
                        ${this.escapeHtml(product.title)}
                      </a>
                      <div class="cart-upsell-card__bottom">
                        <div class="cart-upsell-card__price-wrap">
                          <span class="cart-upsell-card__price">${price}</span>
                          ${compareAt ? `<s class="cart-upsell-card__compare">${compareAt}</s>` : ''}
                        </div>
                        <button
                          type="button"
                          class="cart-upsell-card__button"
                          data-upsell-add
                          data-variant-id="${variant.id}"
                        >
                          ${this.escapeHtml(strings.add)}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              `;
              })
              .join('')}
          </div>
        </div>
      </div>
    `;

    this.ensureAddDelegation();
  },

  initSwiper() {
    const container = this.getContainer();
    if (!container) return;

    const slider = container.querySelector('.cart-upsell-swiper');
    if (!slider || typeof Swiper === 'undefined') return;

    this.destroySwiper();
    this.swiper = new Swiper(slider, {
      slidesPerView: 1,
      spaceBetween: 10,
      speed: 400,
      watchOverflow: true,
      // observer/observeParents intentionally disabled: the list is re-rendered
      // and re-initialised manually on every cart change, so letting Swiper
      // auto-reinit on DOM mutations only causes redundant re-init churn.
      navigation: {
        nextEl: container.querySelector('.cart-upsell-next'),
        prevEl: container.querySelector('.cart-upsell-prev'),
      },
      breakpoints: {
        480: { slidesPerView: 1, spaceBetween: 10 },
        768: { slidesPerView: 1, spaceBetween: 12 },
      },
    });
  },

  destroySwiper() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      try {
        this.swiper.destroy(true, true);
      } catch (error) {
        /* instance already detached — ignore */
      }
    }
    this.swiper = null;
  },

  // Bind the "Add" handler once via event delegation on the document. Because
  // the recommendations list is re-rendered on every cart change, per-button
  // listeners get lost after the first add; delegation keeps it working for
  // every (re-rendered) button without rebinding.
  ensureAddDelegation() {
    if (this._addDelegationBound) return;
    this._addDelegationBound = true;

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-upsell-add]');
      if (!button || !button.closest('#CartDrawer-Recommendations')) return;
      event.preventDefault();
      this.handleAdd(button);
    });
  },

  async handleAdd(button) {
    // Serialize adds: overlapping add + updateCartUI calls race and corrupt the
    // cart-items DOM (which also breaks the quantity minus/plus buttons).
    if (this.isAdding) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    this.isAdding = true;
    button.disabled = true;
    const strings = this.getStrings();
    button.textContent = strings.adding;

    try {
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (addResponse.status === 429) throw new Error('Rate limited (429) — please wait a moment');
      if (!addResponse.ok) throw new Error('Failed to add upsell product');

      // Optimistically drop the just-added product from the cached
      // recommendations so the re-render removes its card without a network call.
      this.removeFromCache(variantId);

      // 429/HTML-safe parse: a rate-limited /cart.js returns HTML, not JSON.
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cartData = cartRes.ok ? await cartRes.json().catch(() => null) : null;

      if (cartData) {
        if (window.updateKikCartDrawerTotal) window.updateKikCartDrawerTotal(cartData);
        if (window.updateCartCount) await window.updateCartCount(cartData.item_count);
      }
      // updateCartUI()/empty_updateCartUI() also trigger refreshCartRecommendations(),
      // which re-renders the list — so no extra refresh call is needed here.
      if (window.updateCartUI) await window.updateCartUI();
      else if (window.empty_updateCartUI) await window.empty_updateCartUI();
    } catch (error) {
      console.error('Upsell add failed:', error);
    } finally {
      this.isAdding = false;
      // The button may have been replaced by the re-render; resetting a detached
      // node is harmless, and the new (delegated) buttons are already live.
      button.disabled = false;
      button.textContent = strings.add;
    }
  },

  clear() {
    const container = this.getContainer();
    if (container) container.innerHTML = '';
    this.getCache()?.clear();
  },

  async refresh(force = false) {
    await this.load(force);
  },

  async getCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    // On a rate-limited (429) or error response the body is HTML, not JSON, so
    // parse defensively and return null rather than throwing a SyntaxError.
    if (!response.ok) return null;
    return response.json().catch(() => null);
  },

  getCartHash(cart) {
    return (cart.items || [])
      .map((item) => `${item.product_id}:${item.variant_id}:${item.quantity}`)
      .join('|');
  },

  async getRecommendationsFromCart(cart) {
    const seeds = this.getRecommendationSeeds(cart);
    const productsBySeed = seeds.map(() => []);
    const seenProductIds = new Set();

    for (const [index, item] of seeds.entries()) {
      const products = await this.fetchRecommendations(item.product_id, 'complementary');
      this.addProductsToSeed(productsBySeed[index], products, cart, seenProductIds);
    }

    let mixedProducts = this.roundRobinProducts(productsBySeed, this.recommendationLimit);
    if (mixedProducts.length >= this.recommendationLimit) return mixedProducts;

    for (const [index, item] of seeds.entries()) {
      const products = await this.fetchRecommendations(item.product_id, 'related');
      this.addProductsToSeed(productsBySeed[index], products, cart, seenProductIds);
    }

    return this.roundRobinProducts(productsBySeed, this.recommendationLimit);
  },

  getRecommendationSeeds(cart) {
    const seeds = [];
    const seenProductIds = new Set();

    for (const item of cart.items || []) {
      const productId = Number(item.product_id);
      if (!productId || seenProductIds.has(productId)) continue;

      seenProductIds.add(productId);
      seeds.push(item);
      if (seeds.length >= this.recommendationSeedLimit) break;
    }

    return seeds;
  },

  addProductsToSeed(seedProducts, products, cart, seenProductIds) {
    this.filterProducts(products, cart).forEach((product) => {
      const id = Number(product.id);
      if (!seenProductIds.has(id)) {
        seenProductIds.add(id);
        seedProducts.push(product);
      }
    });
  },

  roundRobinProducts(productsBySeed, limit) {
    const products = [];
    let index = 0;
    let hasProductAtIndex = true;

    while (products.length < limit && hasProductAtIndex) {
      hasProductAtIndex = false;

      for (const seedProducts of productsBySeed) {
        const product = seedProducts[index];
        if (!product) continue;

        products.push(product);
        hasProductAtIndex = true;
        if (products.length >= limit) break;
      }

      index += 1;
    }

    return products;
  },

  async fetchRecommendations(productId, intent = 'related') {
    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=8&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json().catch(() => null);
    return (data && data.products) || [];
  },

  filterProducts(products, cart) {
    const cartProductIds = cart.items.map((item) => Number(item.product_id));
    const cartVariantIds = cart.items.map((item) => Number(item.variant_id));

    return products.filter((product) => {
      const firstAvailableVariant = product.variants?.find((variant) => variant.available);
      return (
        product.available &&
        !cartProductIds.includes(Number(product.id)) &&
        firstAvailableVariant &&
        !cartVariantIds.includes(Number(firstAvailableVariant.id))
      );
    });
  },

  formatMoney(cents) {
    if (!Number.isFinite(cents)) return '';

    const shopify = window.Shopify || {};
    const moneyFormat = shopify.money_format || window.themeMoneyFormat;

    if (typeof shopify.formatMoney === 'function' && moneyFormat) {
      return shopify.formatMoney(cents, moneyFormat);
    }

    // Parse the store's money_format string (e.g. "C${{amount}}") so the
    // currency symbol configured in the store is used. This avoids Intl's
    // locale symbol (e.g. CAD rendered as "CA$" instead of "C$").
    if (typeof moneyFormat === 'string' && /\{\{\s*\w+\s*\}\}/.test(moneyFormat)) {
      return this.formatMoneyWithFormatString(cents, moneyFormat);
    }

    const currency = shopify.currency?.active || window.cartCurrency || 'USD';
    const amount = cents / 100;
    const noFractionCurrencies = ['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
    const hasFraction = cents % 100 !== 0 && !noFractionCurrencies.includes(currency);

    try {
      return amount.toLocaleString(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0,
      });
    } catch (error) {
      return hasFraction ? `${currency} ${amount.toFixed(2)}` : `${currency} ${Math.round(amount)}`;
    }
  },

  // Standard Shopify money_format parser: substitutes the {{ amount }} style
  // placeholder in the store's format string with the delimited amount.
  formatMoneyWithFormatString(cents, formatString) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      if (!Number.isFinite(number)) return '0';
      const value = (number / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },

  getProductImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.url) return product.featured_image.url;
    if (product.images?.length) return product.images[0];
    return '';
  },

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  },
};

window.addEventListener('load', () => bootCartRecommendations.init());

window.bootCartRecommendations.refresh = async function (force = false) {
  await this.load(force);
};
